<template>
  <div v-if="selectedCodes && selectedCodes.length" class="text-preview-wrapper">
    <transition name="fade-content" mode="out-in">
      <!-- Loading state -->
      <div v-if="showSpinner" key="loader" class="text-preview-loader">
        <loader size="medium" />
      </div>
      <!-- Content -->
      <div
        v-else-if="debouncedStringElements && debouncedStringElements.length"
        key="content"
        class="text-preview"
        ref="textContainer"
      >
        <!-- Small spinner in top right corner during loading -->
        <transition name="fade">
          <div v-if="isLoading && !disabled" class="loading-spinner">
            <loader size="small" />
          </div>
        </transition>
        <transition-group name="list-fade" tag="div" appear>
          <div
            v-for="(group, index) of debouncedStringElements"
            :key="group.key"
            :style="{ transitionDelay: `${index * 0.1}s` }"
          >
            <transition name="fade-fast" appear>
              <div v-if="group.header" class="d-inline-flex align-groups-start">
                <h3>{{ group.header }}</h3>
              </div>
            </transition>
            <transition-group name="item-slide" tag="div" appear>
              <div
                v-for="(item, itemIndex) in group.items"
                :key="item.key"
                class="d-flex item-row"
                :style="{ transitionDelay: `${index * 0.1 + itemIndex * 0.05 + 0.05}s` }"
              >
                <div v-for="n in item.tabs" :key="n">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</div>
                <div v-html="item.text" />
              </div>
            </transition-group>
            <transition name="fade-fast" appear>
              <div
                v-if="commentMap[group.key]"
                class="d-flex"
                :style="{ transitionDelay: `${index * 0.1 + 0.15}s` }"
              >
                <div>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</div>
                <div v-html="commentMap[group.key]" />
              </div>
            </transition>
          </div>
        </transition-group>
      </div>
    </transition>
  </div>
</template>

<script>
import { CytCodeGroupEnum, CytologyModifierMap } from "@/modules/enums";
import cytologyService, { CytologyEntities } from "@/services/cytology";
import { mapState } from "vuex";
import Loader from "@/components/common/Loader.vue";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";

export default {
  name: "CytologyTextPreview",
  components: {
    Loader
  },
  props: {
    selectedCodes: {
      type: Array,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  subscriptions() {
    this.stringElementsSubject$ = new Subject();

    return {
      debouncedStringElements: this.stringElementsSubject$.pipe(
        debounceTime(300),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
      )
    };
  },
  data() {
    return {
      selected: [],
      isLoading: false,
      stringElementsData: [],
      wrapperHeight: '270px'
    };
  },

  watch: {
    selectedCodeKeys: {
      handler: function (newVal) {
        if (newVal?.length) {
          this.loadCodes(newVal);
        }
      },
      deep: true,
      immediate: true
    },
    selected: {
      handler(val) {
        if (!val) return;
        if (!val?.length) return;
        for (const code of val) {
          const selectedCodeIdx = this.selectedCodes.findIndex(c => {
            const id = c.id || c.cytDiagnosticCodeId;
            return id === code.id;
          });
          if (selectedCodeIdx > -1) {
            const selectedItem = this.selectedCodes[selectedCodeIdx];
            if (selectedItem) {
              code.comments = selectedItem.comments;
            }
          }
        }
      }
    },
    stringElements: {
      handler(newVal) {
        if (this.stringElementsSubject$) {
          this.stringElementsSubject$.next(newVal);
        }
      },
      immediate: true
    },
    debouncedStringElements: {
      handler() {
        this.$nextTick(() => {
          this.updateWrapperHeight();
        });
      }
    },
    showSpinner: {
      handler() {
        this.$nextTick(() => {
          this.updateWrapperHeight();
        });
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.updateWrapperHeight();
    });
  },
  beforeDestroy() {
    if (this.stringElementsSubject$) {
      this.stringElementsSubject$.complete();
    }
  },
  methods: {
    async loadCodes(selection) {
      this.isLoading = true;
      const codes = new Set(selection.map(c => c?.id || c?.cytDiagnosticCodeId || c));
      if (codes?.size) {
        const items = Array.from(codes);
        this.selected = await Promise.all(
          items.map(id => cytologyService.getSingleEntity(CytologyEntities.CytDiagnosticCodes, id))
        );
      }
      this.isLoading = false;
      this.firstLoad = false;
    },
    updateWrapperHeight() {
      const wrapper = this.$el;
      if (!wrapper) return;

      // Temporarily set height to auto to measure natural height
      wrapper.style.height = 'auto';
      const naturalHeight = wrapper.scrollHeight;
      
      // Ensure minimum height
      const minHeight = 270;
      const targetHeight = Math.max(naturalHeight, minHeight);
      
      // Set the height explicitly for smooth transition
      wrapper.style.height = `${targetHeight}px`;
    }
  },
  computed: {
    ...mapState({
      adequacyTerm: state => state.labSettings.CytologyTermAdequacy,
      commentTerm: state => state.labSettings.CytologyTermComment,
      diagnosisTerm: state => state.labSettings.CytologyTermDiagnosis,
      recommendationTerm: state => state.labSettings.CytologyTermRecommendation,
      hormonalTerm: state => state.labSettings.CytologyTermHormonal
    }),
    enabled() {
      return !this.disabled;
    },
    showSpinner() {
      return this.isLoading && this.disabled;
    },
    selectedCodeKeys() {
      return this.selectedCodes.map(c => {
        if (typeof c === "number") {
          return c;
        } else {
          return c.id || c.cytDiagnosticCodeId;
        }
      });
    },
    parentModifierMap() {
      return Object.keys(CytologyModifierMap).reduce((acc, key) => {
        acc[CytologyModifierMap[key]] = key;
        return acc;
      }, {});
    },
    typeMap() {
      return Object.keys(CytCodeGroupEnum).reduce((acc, key) => {
        const name = this.nameMap[CytCodeGroupEnum[key]];
        acc[CytCodeGroupEnum[key]] = name;
        return acc;
      }, {});
    },
    topCodes() {
      return [
        CytCodeGroupEnum.Adequacy,
        CytCodeGroupEnum.Diagnosis,
        CytCodeGroupEnum.Comment,
        CytCodeGroupEnum.GeneralCategorization,
        CytCodeGroupEnum.Hormonal,
        CytCodeGroupEnum.Recommendation
      ];
    },
    nameMap() {
      return {
        [CytCodeGroupEnum.Adequacy]: this.adequacyTerm || "Adequacy",
        [CytCodeGroupEnum.Diagnosis]: this.diagnosisTerm || "Diagnosis",
        [CytCodeGroupEnum.Comment]: this.commentTerm || "Comment",
        [CytCodeGroupEnum.GeneralCategorization]: "General Categorization",
        [CytCodeGroupEnum.Hormonal]: this.hormonalTerm || "Hormonal",
        [CytCodeGroupEnum.Recommendation]: this.recommendationTerm || "Recommendation"
      };
    },
    commentMap() {
      const output = {};
      for (const code of this.selected) {
        const groupId = code.cytCodeGroupId;
        if (this.topCodes.includes(Number(groupId))) {
          if (code.comments) {
            output[code.id] = code.comments;
          }
        }
      }
      return output;
    },
    stringElements() {
      const elements = [];
      if (!Array.isArray(this.selected)) {
        return elements;
      }

      // Initialize groups first to maintain structure
      const groupedCodes = {};
      const groupElements = {};

      // First pass: Initialize groups for parent codes
      for (const code of this.selected) {
        const groupId = code.cytCodeGroupId;
        if (this.topCodes.includes(Number(groupId)) && !groupElements[groupId]) {
          const groupHeader = `**${this.typeMap[groupId]}**`;
          const groupItem = {
            tabs: 0,
            key: groupId,
            items: [],
            header: groupHeader
          };
          groupElements[groupId] = groupItem;
          elements.push(groupItem);
          groupedCodes[groupId] = [];
        }
      }

      // Second pass: Process items in original order
      for (const item of this.selected) {
        const groupId = item.cytCodeGroupId;

        // Handle parent code items
        if (this.topCodes.includes(Number(groupId))) {
          const codeItem = {
            tabs: 2,
            text: item.formattedText,
            id: item.id,
            key: item.id
          };

          groupElements[groupId].items.push(codeItem);

          // if (item.comments) {
          //   groupElements[groupId].items.push({
          //     tabs: 2,
          //     text: item.comments,
          //     key: `${item.id}-comment`
          //   });
          // }
        }
        // Handle modifiers
        else {
          const parentType = this.parentModifierMap[groupId];
          if (parentType && groupElements[parentType]) {
            const modifierElement = {
              tabs: 3,
              text: item.formattedText,
              key: item.id
            };
            groupElements[parentType].items.push(modifierElement);
          }
        }
      }

      return elements;
    }
  }
};
</script>

<style lang="scss" scoped>
.text-preview-wrapper {
  position: relative;
  min-height: 270px;
  margin-top: 1rem;
  transition: height 0.4s ease;
  overflow: hidden;
}

.text-preview {
  border: 1px solid hsl(0, 0%, 90%);
  border-radius: 5px;
  background: hsl(50, 100%, 80%);
  padding: 1rem 0.5rem;
  position: relative;
  min-height: 270px;
}

.text-preview-loader {
  border: 1px solid hsl(0, 0%, 90%);
  border-radius: 5px;
  background: hsl(50, 100%, 80%);
  min-height: 270px;
  display: flex;
  align-items: center;
  justify-content: center;
}

// Content transition
.fade-content-enter-active,
.fade-content-leave-active {
  transition: opacity 0.3s ease;
}

.fade-content-enter {
  opacity: 0;
}

.fade-content-leave-to {
  opacity: 0;
}

.loading-spinner {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 10;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  padding: 0.25rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

// Transition classes for groups
.list-fade-enter-active,
.list-fade-leave-active,
.list-fade-move {
  transition: all 0.3s ease;
}

.list-fade-enter {
  opacity: 0;
  transform: translateY(-10px);
}

.list-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

// Transition classes for individual items
.item-slide-enter-active,
.item-slide-leave-active,
.item-slide-move {
  transition: all 0.25s ease;
}

.item-slide-enter {
  opacity: 0;
  transform: translateX(-20px);
}

.item-slide-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

// Ensure smooth layout during transitions
.item-row {
  will-change: transform, opacity;
}

.plus-icon {
  position: absolute;
  top: 0;
  right: 0;
}

::v-deep .diagnosis-code {
  padding: 0;
}
.name {
  font-size: 1.15rem;
  font-weight: bold;
}
.list {
  max-height: 300px;
  overflow-y: auto;
}

dl {
  display: flex;
  color: hsl(0, 0%, 40%);
  column-gap: 0.5rem;
}

dt {
  font-weight: 400;
  margin-bottom: 0.25rem;
}
</style>
